/**
 * ProjectTypeComprehensionBuilder
 *
 * Pure utilities the HPC config app uses when a user creates/edits a PROJECT TYPE or
 * CONTRACT TYPE:
 *   - `build{Project,Contract}TypeComprehension` assemble the comprehension that
 *     creates/updates the type record + its prototype project/contract shell.
 *   - `computeDefaultDateReconciliation` produces the upsert/delete plan that keeps the
 *     type's default-date list (and the prototype's matching dates) in sync with the
 *     editor grid WITHOUT churning untouched rows or deleting user data.
 *
 * The type identifies its prototype by the `Settings.PrototypeID{Project,Contract}`
 * pointer written back after the push — the prototype carries no marker. Statuses,
 * allowed project types, and default dates are persisted by the caller AFTER the push
 * (they need the type's / prototype's numeric ids, unknown until the push creates them).
 *
 * DEFAULT DATES are deliberately NOT part of the comprehension. The type's default-date
 * definitions (`{Project,Contract}TypeDefaultDate`) and the prototype's matching dates
 * (`{Project,Contract}Date`) are reconciled by Hash (their natural key) so an unchanged
 * date keeps its row/id, an added row is created, and ONLY a row removed from the grid is
 * deleted. Crucially, dates a user added directly to the prototype (a Hash that was never
 * a default) are never touched — see `computeDefaultDateReconciliation`.
 *
 * Cross-entity links use external GUIDs the comprehension processor resolves at push
 * time: the prototype references the type via `GUID{Project,Contract}Type`. The caller
 * supplies random GUIDs (the existing record GUIDs on edit; freshly minted ones on
 * create) for `typeGUID` / `prototypeGUID` — opaque identity keys, never derived from
 * Hash, so a Hash reused across types or customers never collides.
 *
 * No Fable / pict dependencies — unit-tested in isolation.
 */

/** Stable, filesystem/url-safe-ish slug of a natural key. */
function slug(pValue)
{
	return String(pValue === null || pValue === undefined ? '' : pValue)
		.trim()
		.replace(/[^A-Za-z0-9_-]+/g, '_')
		.slice(0, 48);
}

/**
 * @typedef {Object} ProjectTypeComprehensionInput
 * @property {string} typeGUID - external GUID for the ProjectType (existing GUIDProjectType on edit, new on create).
 * @property {string} prototypeGUID - external GUID for the prototype Project.
 * @property {Object} type - { Name, Hash, Description, Ordinal, IDCustomer, IDProgram, Settings } (Settings = scalars only, no statuses/dates).
 */

/**
 * Build the project-type comprehension: the `ProjectType` record + its prototype
 * `Project` shell. Default dates are reconciled separately (see
 * `computeDefaultDateReconciliation`); the prototype's forms / observation types /
 * labels are configured through the normal project tools and cloned by the seeder.
 *
 * @param {ProjectTypeComprehensionInput} pInput
 * @returns {Object} comprehension keyed by entity name -> external GUID -> record
 */
function buildProjectTypeComprehension(pInput)
{
	const tmpInput = pInput || {};
	const tmpType = tmpInput.type || {};
	const tmpTypeGUID = tmpInput.typeGUID;
	const tmpProtoGUID = tmpInput.prototypeGUID;
	if (!tmpTypeGUID || !tmpProtoGUID)
	{
		throw new Error('buildProjectTypeComprehension requires typeGUID and prototypeGUID.');
	}
	const tmpCustomer = tmpType.IDCustomer || 0;
	const tmpProgram = tmpType.IDProgram || 0;
	const tmpTypeName = tmpType.Name || '';

	// The type's Settings blob carries scalars + the prototype pointer (by GUID; the
	// numeric PrototypeIDProject is resolved + written by the caller after the push).
	const tmpSettings = Object.assign({}, tmpType.Settings || {}, { PrototypeGUIDProject: tmpProtoGUID });

	const tmpComprehension = {};

	tmpComprehension.ProjectType =
	{
		[tmpTypeGUID]:
		{
			GUIDProjectType: tmpTypeGUID,
			Name: tmpTypeName,
			Hash: tmpType.Hash || '',
			Description: tmpType.Description || '',
			Ordinal: tmpType.Ordinal || 0,
			IDCustomer: tmpCustomer,
			IDProgram: tmpProgram,
			SettingsJSON: JSON.stringify(tmpSettings),
		},
	};

	// Prototype Project shell — references the type via GUIDProjectType. The type is the
	// authority on which project is its prototype (ProjectType.Settings.PrototypeIDProject,
	// written back after this push), so no marker is stamped on the project.
	tmpComprehension.Project =
	{
		[tmpProtoGUID]:
		{
			GUIDProject: tmpProtoGUID,
			GUIDProjectType: tmpTypeGUID,
			Name: `[PROTOTYPE] ${tmpTypeName || tmpType.Hash || 'Project Type'}`,
			Description: `Canonical prototype project for project type "${tmpTypeName || tmpType.Hash}". Managed by the project-type configuration.`,
			IDCustomer: tmpCustomer,
		},
	};

	return tmpComprehension;
}

/**
 * Build the contract-type comprehension: the `ContractType` record + its prototype
 * `Contract` shell. Default dates are reconciled separately (see
 * `computeDefaultDateReconciliation`).
 *
 * @param {{typeGUID:string, prototypeGUID:string, type:Object}} pInput
 * @returns {Object} comprehension keyed by entity name -> external GUID -> record
 */
function buildContractTypeComprehension(pInput)
{
	const tmpInput = pInput || {};
	const tmpType = tmpInput.type || {};
	const tmpTypeGUID = tmpInput.typeGUID;
	const tmpProtoGUID = tmpInput.prototypeGUID;
	if (!tmpTypeGUID || !tmpProtoGUID)
	{
		throw new Error('buildContractTypeComprehension requires typeGUID and prototypeGUID.');
	}
	const tmpCustomer = tmpType.IDCustomer || 0;
	const tmpTypeName = tmpType.Name || '';
	const tmpSettings = Object.assign({}, tmpType.Settings || {}, { PrototypeGUIDContract: tmpProtoGUID });

	const tmpComprehension = {};
	tmpComprehension.ContractType =
	{
		[tmpTypeGUID]:
		{
			GUIDContractType: tmpTypeGUID,
			Name: tmpTypeName,
			Hash: tmpType.Hash || '',
			Description: tmpType.Description || '',
			IDCustomer: tmpCustomer,
			SettingsJSON: JSON.stringify(tmpSettings),
		},
	};
	tmpComprehension.Contract =
	{
		[tmpProtoGUID]:
		{
			GUIDContract: tmpProtoGUID,
			GUIDContractType: tmpTypeGUID,
			Name: `[PROTOTYPE] ${tmpTypeName || tmpType.Hash || 'Contract Type'}`,
			IDCustomer: tmpCustomer,
		},
	};

	return tmpComprehension;
}

/** Trim + coerce a raw display value to a string. */
function cleanString(pValue)
{
	return (typeof pValue === 'string') ? pValue.trim() : '';
}

/**
 * Normalize the editor's default-date rows to `[{ Hash, Name }]` keyed by a non-empty
 * Hash (falling back to Name when Hash is blank), de-duplicated by Hash (last wins).
 *
 * @param {Array<{Hash?:string,Name?:string}>} pGridDates
 * @returns {Map<string,{Hash:string,Name:string}>}
 */
function normalizeGridDatesByHash(pGridDates)
{
	const tmpByHash = new Map();
	const tmpRows = Array.isArray(pGridDates) ? pGridDates : [];
	for (const tmpRow of tmpRows)
	{
		const tmpName = cleanString(tmpRow && tmpRow.Name);
		const tmpHash = cleanString(tmpRow && tmpRow.Hash) || tmpName;
		if (!tmpHash)
		{
			continue;
		}
		tmpByHash.set(tmpHash, { Hash: tmpHash, Name: tmpName || tmpHash });
	}
	return tmpByHash;
}

/** Index existing rows by their (non-blank) Hash; last row for a Hash wins. */
function indexRowsByHash(pRows)
{
	const tmpByHash = new Map();
	const tmpRows = Array.isArray(pRows) ? pRows : [];
	for (const tmpRow of tmpRows)
	{
		const tmpHash = cleanString(tmpRow && tmpRow.Hash);
		if (tmpHash)
		{
			tmpByHash.set(tmpHash, tmpRow);
		}
	}
	return tmpByHash;
}

/**
 * Compute the upsert/delete plan to reconcile a type's default dates with the editor grid
 * — applied by `TypeComprehensionSaveProvider`. Reconciliation is by Hash (the date
 * milestone's natural key):
 *   - an unchanged grid row produces NO write (its row + id are left intact),
 *   - a grid row whose Name changed produces an UPDATE that keeps the existing id,
 *   - a brand-new grid row produces a CREATE,
 *   - filled prototype dates keep their `Date` when their Name is updated.
 *
 * The type's own definition list (`*TypeDefaultDate`) is fully reconciled — a row removed
 * from the grid is DELETED, since the grid is that list's editor. The prototype's dates
 * (`{Project,Contract}Date`), by contrast, are ONLY added/updated and NEVER deleted: a
 * prototype is a real project/contract a user configures separately, so removing a row
 * from the grid simply drops it from the default list while leaving the prototype's date
 * (now a non-default date the user owns) untouched.
 *
 * @param {Array<Object>} pExistingTypeRows - current `{Project,Contract}TypeDefaultDate` rows for the type.
 * @param {Array<Object>} pExistingProtoRows - current `{Project,Contract}Date` rows on the prototype.
 * @param {Array<{Hash?:string,Name?:string}>} pGridDates - the editor's current date list.
 * @param {{typeDateEntity:string, protoDateEntity:string, typeIDField:string, protoIDField:string, typeID:number, protoID:number, idCustomer:number}} pKeys
 * @returns {{typeUpserts:Array<Object>, typeDeletes:Array<number>, protoUpserts:Array<Object>}}
 */
function computeDefaultDateReconciliation(pExistingTypeRows, pExistingProtoRows, pGridDates, pKeys)
{
	const tmpKeys = pKeys || {};
	const tmpTypeIDKey = `ID${tmpKeys.typeDateEntity}`;
	const tmpProtoIDKey = `ID${tmpKeys.protoDateEntity}`;

	const tmpGridByHash = normalizeGridDatesByHash(pGridDates);
	const tmpTypeByHash = indexRowsByHash(pExistingTypeRows);
	const tmpProtoByHash = indexRowsByHash(pExistingProtoRows);

	const tmpPlan = { typeUpserts: [], typeDeletes: [], protoUpserts: [] };

	for (const [tmpHash, tmpDate] of tmpGridByHash)
	{
		const tmpExistingType = tmpTypeByHash.get(tmpHash);
		if (!tmpExistingType)
		{
			tmpPlan.typeUpserts.push({ Name: tmpDate.Name, Hash: tmpHash, [tmpKeys.typeIDField]: tmpKeys.typeID, IDCustomer: tmpKeys.idCustomer });
		}
		else if (cleanString(tmpExistingType.Name) !== tmpDate.Name)
		{
			tmpPlan.typeUpserts.push({ [tmpTypeIDKey]: tmpExistingType[tmpTypeIDKey], Name: tmpDate.Name, Hash: tmpHash, [tmpKeys.typeIDField]: tmpKeys.typeID, IDCustomer: tmpKeys.idCustomer });
		}

		const tmpExistingProto = tmpProtoByHash.get(tmpHash);
		if (!tmpExistingProto)
		{
			tmpPlan.protoUpserts.push({ Name: tmpDate.Name, Hash: tmpHash, [tmpKeys.protoIDField]: tmpKeys.protoID, Date: null, IDCustomer: tmpKeys.idCustomer });
		}
		else if (cleanString(tmpExistingProto.Name) !== tmpDate.Name)
		{
			// Preserve a filled Date when only the Name changed.
			tmpPlan.protoUpserts.push({ [tmpProtoIDKey]: tmpExistingProto[tmpProtoIDKey], Name: tmpDate.Name, Hash: tmpHash, [tmpKeys.protoIDField]: tmpKeys.protoID, Date: (tmpExistingProto.Date != null ? tmpExistingProto.Date : null), IDCustomer: tmpKeys.idCustomer });
		}
	}

	// The type's own default-date list is fully reconciled — delete the rows the user
	// removed from the grid. Prototype dates are intentionally never deleted (see above).
	for (const tmpRow of (Array.isArray(pExistingTypeRows) ? pExistingTypeRows : []))
	{
		const tmpHash = cleanString(tmpRow && tmpRow.Hash);
		if (tmpHash && !tmpGridByHash.has(tmpHash) && tmpRow[tmpTypeIDKey])
		{
			tmpPlan.typeDeletes.push(tmpRow[tmpTypeIDKey]);
		}
	}

	return tmpPlan;
}

module.exports = {
	slug,
	buildProjectTypeComprehension,
	buildContractTypeComprehension,
	normalizeGridDatesByHash,
	computeDefaultDateReconciliation,
};
